import { player } from '@/lib/player.ts';
import { BUFFER_PROFILES, useBuffer } from '@/stores/buffer.ts';
import { Focusable } from '@/ui/Focusable.tsx';

/**
 * Buffer size, as a setting rather than a constant.
 *
 * Changing it re-tunes immediately: a buffer setting you cannot hear the
 * effect of is a buffer setting nobody will ever trust. The player rebuilds
 * around the new profile, which on the native path means a new LoadControl.
 *
 * TWO VARIANTS, RENDERED DIFFERENTLY RATHER THAN RESTYLED.
 *
 * `chip` is the dense toolbar form this was built as. `row` is a dialog row,
 * identical to every other item in the menu it sits in.
 *
 * The dialog form used to be the chip with a pile of CSS overrides on top,
 * and it never came out right — the selected item sat visibly offset from its
 * neighbours, because two style systems were fighting over one element and
 * whichever won, something was left over. Rendering the right markup is
 * shorter than overriding the wrong markup, and it cannot drift: a row IS a
 * `.menu-item`, so it inherits every fix that one ever gets.
 */
export function BufferPicker({
  onPicked, variant = 'chip',
}: {
  onPicked?(): void;
  variant?: 'chip' | 'row';
} = {}) {
  const { id, set } = useBuffer();
  const row = variant === 'row';

  return (
    <div className={row ? 'menu-items' : 'bufferpicker'} role="group" aria-label="Buffer">
      {BUFFER_PROFILES.map((p) => (
        <Focusable
          key={p.id}
          id={`buffer-${p.id}`}
          className={row
            ? `btn menu-item ${id === p.id ? 'active' : ''}`
            : `swatch ${id === p.id ? 'active' : ''}`}
          onSelect={() => {
            set(p.id);
            const channel = player.getState().channel;
            if (channel) void player.play(channel).catch(() => { /* shown in state */ });
            onPicked?.();
          }}
        >
          {!row && (
            <span className="bars" aria-hidden="true">
              <i /><i /><i />
            </span>
          )}
          <span className="label">{p.name}</span>
          {/* The hint earns its place only where there is room to read it. */}
          {row && <span className="menu-value">{p.hint}</span>}
        </Focusable>
      ))}
    </div>
  );
}
